import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Analytics from '@ublend-npm/aula-analytics';
import List, {
  ListItem,
  ListItemContent,
  ListItemPrimaryText,
  ListItemSecondaryText,
  RightDecoration,
  RightDecorations,
} from '$components/List';
import ExportGradesButton from '$components/ExportGradesButton';

import * as TYPES from '$variables/types';
import AssignmentMappingsEditModal from '$components/AssignmentMappingsEditModal';
import Spinner from '$components/Spinner';
import ExportPanel from '$components/ExportPanel';
import checkExportJobInProgress from '$utils/checkExportJobInProgress';
import getAssignmentMappingsSubtitle from '$utils/getAssignmentMappingsSubtitle';
import getAssessmentItemMappingsSubtitle from '$utils/getAssessmentItemMappingsSubtitle';
import { raiseToast } from '$components/Toast';
import { EDUCATOR } from '$variables/constants';

import { Wrapper } from './AssessmentItems.styled';
import { OPEN_MAP_ASSIGNMENTS_MODAL } from '$variables/analyticsEvents';
import shouldShowExportButton from './utils/shouldShowExportButton';

const AssessmentItems = ({
  assessmentItems,
  assignments,
  mapAssignments,
  clearMapAssignmentsError,
  mapAssignmentsError,
  mapAssignmentsLoading,
  mapAssignmentsSuccessful,
  isGeneratingCsv,
  exportGrades,
  isInitiatingExport,
  assessmentItemsLoading,
  isGradePassBackDisabled,
  viewAs,
}) => {
  const [mapAssignmentModalVisible, setMapAssignmentModalVisible] =
    useState(false);
  const [selectedAssessmentItem, setSelectedAssessmentItem] = useState(null);
  const [assessmentItemId, selectAssessmentItem] = useState(null);

  const handleClose = (event) => {
    if (event) event.preventDefault();
    setMapAssignmentModalVisible(false);
    setSelectedAssessmentItem(null);
    clearMapAssignmentsError();
  };

  const openExportPanel = (id) => () => {
    selectAssessmentItem(id);
  };

  const closeExportPanel = () => {
    selectAssessmentItem(null);
  };

  const handleGradeExport = async (overrides) => {
    try {
      await exportGrades({ assessmentItemId, overrides });
      closeExportPanel();
    } catch (e) {
      raiseToast({ message: 'Oops! Something went wrong.', type: 'fail' });
    }
  };

  const createHandleAssessmentItemClick = (item) => () => {
    setSelectedAssessmentItem(item);
    setMapAssignmentModalVisible(true);

    Analytics.track({
      event: OPEN_MAP_ASSIGNMENTS_MODAL,
      assessmentItemId: item.id,
    });
  };

  if (assessmentItemsLoading) {
    return <Spinner />;
  }

  const isLoading =
    mapAssignmentsLoading || isGeneratingCsv || isInitiatingExport;

  const isExportButtonDisabled =
    isGradePassBackDisabled || isLoading || viewAs !== EDUCATOR;

  return (
    !!assessmentItems.length && (
      <Wrapper>
        <List headerText="Assessment items" testId="assessment-items-list">
          {assessmentItems.map((item) => {
            const {
              id,
              title,
              mappedAssignments = [],
              linkedAssessments = [],
              lastJob,
            } = item;

            const isExportButtonDisplayed = shouldShowExportButton({
              mappedAssignments,
              assignments,
            });

            return (
              <ListItem
                key={id}
                onClick={createHandleAssessmentItemClick(item)}
              >
                <ListItemContent>
                  <ListItemPrimaryText>{title}</ListItemPrimaryText>
                  <ListItemSecondaryText>
                    {getAssessmentItemMappingsSubtitle(linkedAssessments)}
                  </ListItemSecondaryText>
                  <ListItemSecondaryText>
                    {getAssignmentMappingsSubtitle(mappedAssignments)}
                  </ListItemSecondaryText>
                </ListItemContent>
                {isExportButtonDisplayed && (
                  <RightDecorations>
                    <RightDecoration>
                      <ExportGradesButton
                        onExportGrades={openExportPanel(id)}
                        assessmentItemTitle={title}
                        isDisabled={isExportButtonDisabled}
                        lastJob={lastJob}
                      />
                    </RightDecoration>
                  </RightDecorations>
                )}
              </ListItem>
            );
          })}
        </List>
        {selectedAssessmentItem && (
          <AssignmentMappingsEditModal
            headerText="Assessment item"
            open={mapAssignmentModalVisible}
            assessmentItems={assessmentItems}
            handleClose={handleClose}
            assignments={assignments}
            submitText="Save"
            handleSave={mapAssignments}
            assessmentItem={selectedAssessmentItem}
            error={mapAssignmentsError}
            isLoading={isLoading}
            isSuccessful={mapAssignmentsSuccessful}
            viewAs={viewAs}
            isExportInProgress={checkExportJobInProgress(
              selectedAssessmentItem.lastJob
            )}
          />
        )}
        <ExportPanel
          key={assessmentItemId}
          assessmentItemId={assessmentItemId}
          open={!!assessmentItemId}
          onClose={closeExportPanel}
          onExportToSRS={handleGradeExport}
        />
      </Wrapper>
    )
  );
};

AssessmentItems.propTypes = {
  assessmentItems: PropTypes.arrayOf(TYPES.AssessmentItem).isRequired,
  assignments: PropTypes.arrayOf(TYPES.Assignment).isRequired,
  mapAssignments: PropTypes.func.isRequired,
  clearMapAssignmentsError: PropTypes.func.isRequired,
  mapAssignmentsError: PropTypes.string,
  mapAssignmentsLoading: PropTypes.bool.isRequired,
  mapAssignmentsSuccessful: PropTypes.bool.isRequired,
  isGeneratingCsv: PropTypes.bool.isRequired,
  exportGrades: PropTypes.func.isRequired,
  isInitiatingExport: PropTypes.bool.isRequired,
  assessmentItemsLoading: PropTypes.bool,
  isGradePassBackDisabled: PropTypes.bool,
  viewAs: TYPES.ViewAs,
};

AssessmentItems.defaultProps = {
  mapAssignmentsError: null,
  assessmentItemsLoading: false,
  isGradePassBackDisabled: false,
  viewAs: EDUCATOR,
};

export default AssessmentItems;
